/**
 * EligibilityRecheckPage.tsx — Monthly CMS eligibility re-check results (Story 9.7).
 *
 * PHI endpoint — memberId is displayed. Route is gated to staff:clinical-ops only.
 *
 * ACs covered:
 * - AC 6 (reporting): month selector, pass/fail/unavailable summary cards, paginated results table
 * - Shows memberId (truncated), recheckMonth, outcome (coloured badge), failureReason,
 *   actionTaken, checkedAt
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  type EligibilityRecheckResult,
  type RecheckMonthlySummary,
  type RecheckOutcome,
  getEligibilityRecheckSummary,
  listEligibilityRecheckResults,
} from './eligibility-recheck-api.js';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function currentYearMonth(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function outcomeBadge(outcome: RecheckOutcome): JSX.Element {
  const classes: Record<RecheckOutcome, string> = {
    pass: 'bg-green-100 text-green-800',
    fail: 'bg-red-100 text-red-800',
    unavailable: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${classes[outcome]}`}>
      {outcome}
    </span>
  );
}

function truncateMemberId(id: string): string {
  // Show first 8 + last 4 chars so it's recognisable but compact
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SummaryCardsProps {
  summary: RecheckMonthlySummary | null;
  loading: boolean;
}

function SummaryCards({ summary, loading }: SummaryCardsProps): JSX.Element {
  const cards = [
    {
      label: 'Pass',
      value: summary?.pass ?? 0,
      colorClass: 'text-green-700',
      bgClass: 'bg-green-50 border-green-200',
    },
    {
      label: 'Fail',
      value: summary?.fail ?? 0,
      colorClass: 'text-red-700',
      bgClass: 'bg-red-50 border-red-200',
    },
    {
      label: 'Unavailable',
      value: summary?.unavailable ?? 0,
      colorClass: 'text-yellow-700',
      bgClass: 'bg-yellow-50 border-yellow-200',
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {cards.map((card) => (
        <div key={card.label} className={`border rounded-lg px-6 py-4 ${card.bgClass}`}>
          <p className="text-sm font-medium text-gray-600">{card.label}</p>
          <p className={`text-3xl font-bold mt-1 ${card.colorClass}`}>
            {loading ? '—' : card.value.toLocaleString()}
          </p>
        </div>
      ))}
    </div>
  );
}

interface ResultsTableProps {
  results: EligibilityRecheckResult[];
  loading: boolean;
  error: string | null;
}

function ResultsTable({ results, loading, error }: ResultsTableProps): JSX.Element {
  if (error) {
    return <p className="text-red-600 text-sm py-4">{error}</p>;
  }
  if (!loading && results.length === 0) {
    return <p className="text-gray-500 text-sm py-4">No re-check results for this month.</p>;
  }

  return (
    <Table.ScrollArea loading={loading} loadingLabel="Loading results…">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Member ID</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Recheck Month</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Outcome</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Failure Reason</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Action Taken</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Checked At</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {results.map((r) => (
            <Table.Row key={r.id} className="hover:bg-gray-50">
              <Table.Cell className="font-mono text-xs text-gray-700" title={r.memberId}>
                {truncateMemberId(r.memberId)}
              </Table.Cell>
              <Table.Cell className="text-gray-600">{r.recheckMonth}</Table.Cell>
              <Table.Cell>{outcomeBadge(r.outcome)}</Table.Cell>
              <Table.Cell className="text-gray-600">{r.failureReason ?? '—'}</Table.Cell>
              <Table.Cell>
                <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                  {r.actionTaken}
                </span>
              </Table.Cell>
              <Table.Cell className="whitespace-nowrap text-gray-600">
                {formatDate(r.checkedAt)}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function EligibilityRecheckPage(): JSX.Element {
  const auth = usePortalAuth();
  const accessToken = auth.user?.access_token ?? '';

  const [month, setMonth] = useState<string>(currentYearMonth);
  const [summary, setSummary] = useState<RecheckMonthlySummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [results, setResults] = useState<EligibilityRecheckResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const loadData = useCallback(
    async (selectedMonth: string): Promise<void> => {
      if (!accessToken) return;

      setSummaryLoading(true);
      setSummaryError(null);
      setResultsLoading(true);
      setResultsError(null);
      setResults([]);
      setNextCursor(null);
      setTotalCount(0);

      // Load summary and first page in parallel
      const [summaryResult, resultsResult] = await Promise.allSettled([
        getEligibilityRecheckSummary(accessToken, selectedMonth),
        listEligibilityRecheckResults(accessToken, selectedMonth),
      ]);

      setSummaryLoading(false);
      setResultsLoading(false);

      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value);
      } else {
        setSummaryError(
          summaryResult.reason instanceof Error
            ? summaryResult.reason.message
            : 'Failed to load summary',
        );
      }

      if (resultsResult.status === 'fulfilled') {
        setResults(resultsResult.value.items);
        setNextCursor(resultsResult.value.nextCursor);
        setTotalCount(resultsResult.value.totalCount);
      } else {
        setResultsError(
          resultsResult.reason instanceof Error
            ? resultsResult.reason.message
            : 'Failed to load results',
        );
      }
    },
    [accessToken],
  );

  const loadNextPage = useCallback(async (): Promise<void> => {
    if (!accessToken || !nextCursor || resultsLoading) return;

    setResultsLoading(true);
    setResultsError(null);

    try {
      const data = await listEligibilityRecheckResults(accessToken, month, nextCursor);
      setResults((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch (err) {
      setResultsError(err instanceof Error ? err.message : 'Failed to load more results');
    } finally {
      setResultsLoading(false);
    }
  }, [accessToken, month, nextCursor, resultsLoading]);

  useEffect(() => {
    void loadData(month);
  }, [month, loadData]);

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Eligibility Re-check Results</h1>
        <p className="mt-1 text-gray-500 text-sm">
          Monthly CMS eligibility re-check outcomes for enrolled members. PHI — clinical-ops access
          only.
        </p>
      </div>

      {/* Month selector */}
      <div className="mb-6 flex items-center gap-3">
        <label htmlFor="recheck-month" className="text-sm font-medium text-gray-700">
          Month
        </label>
        <input
          id="recheck-month"
          type="month"
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
          }}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => {
            void loadData(month);
          }}
          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      {summaryError ? (
        <p className="text-red-600 text-sm mb-4">{summaryError}</p>
      ) : (
        <SummaryCards summary={summary} loading={summaryLoading} />
      )}

      {/* Results table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-700">
            Results
            {totalCount > 0 && (
              <span className="ml-2 text-gray-400">({totalCount.toLocaleString()} total)</span>
            )}
          </h2>
        </div>
        <div className="p-4">
          <ResultsTable results={results} loading={resultsLoading} error={resultsError} />
        </div>
        {nextCursor && !resultsLoading && (
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                void loadNextPage();
              }}
              className="text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
            >
              Load more…
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
